import React, { useState, useEffect, useMemo } from 'react';
import { fetchTemplates, sendCampaign, listCampaigns, getCampaign, validateNumbers, deleteCampaign, uploadWaCampaignMedia } from '../services/api';
import DashboardContainer from '../components/layout/DashboardContainer';
import TemplatePreview from '../components/templates/TemplatePreview';

const CampaignsPage = () => {
  const [templates, setTemplates] = useState([]);
  const [campaignName, setCampaignName] = useState('');  // Add campaign name field
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [language, setLanguage] = useState('en_US');
  const [recipients, setRecipients] = useState('');
  const [csvText, setCsvText] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [phoneColumn, setPhoneColumn] = useState('');
  const [varColumns, setVarColumns] = useState([]); // array of header names corresponding to {{1}}, {{2}}, ...
  const [mediaType, setMediaType] = useState(''); // image|video|audio|document|''
  const [mediaLink, setMediaLink] = useState('');
  const [headerMediaMetaId, setHeaderMediaMetaId] = useState('');
  const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState('');
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaUploadError, setMediaUploadError] = useState('');
  const [schedule, setSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(''); // datetime-local string
  const [validated, setValidated] = useState({ total: 0, valid: 0, invalid: 0, samples: [], allResults: [] });
  const [useMetaValidation, setUseMetaValidation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [pollingId, setPollingId] = useState(null);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const { data } = await fetchTemplates({ status: 'approved' });
        const approved = Array.isArray(data) ? data : [];
        setTemplates(approved);

        if (approved.length === 0) {
          setSelectedTemplate('');
          setError('No approved templates available yet. Please wait for Meta approval.');
          return;
        }

        setError('');
        setSelectedTemplate(prev => {
          const existing = approved.find(t => t.name === prev);
          if (existing) {
            return prev;
          }
          const fallback = approved[0];
          setLanguage(fallback?.language || 'en_US');
          return fallback?.name || '';
        });
      } catch (err) {
        setError('Failed to load message templates. Please ensure your account is properly connected.');
        console.error(err);
      }
    };
    loadTemplates();
    // Load campaigns
    (async () => {
      try {
        const { data } = await listCampaigns();
        setCampaigns(data);
      } catch (e) {
        console.warn('Failed to load campaigns');
      }
    })();
  }, []);

  useEffect(() => {
    // Poll the latest few campaigns for metric updates every 5s
    if (pollingId) return;
    const id = setInterval(async () => {
      try {
        const { data } = await listCampaigns();
        setCampaigns(data);
      } catch {}
    }, 5000);
    setPollingId(id);
    return () => clearInterval(id);
  }, [pollingId]);

  const handleSend = async (e) => {
    e.preventDefault();
    
    // Build entries from manual input + CSV
    const entries = [];
    const listFromText = recipients.split(/[\n,;]+/).map(r => r.trim()).filter(r => r);
    for (const p of listFromText) entries.push({ phone: p, vars: [] });
    
    if (rows.length && phoneColumn) {
      for (const r of rows) {
        const phone = String(r[phoneColumn] || '').trim();
        if (!phone) continue;
        const activeColumns = expectedParamsCount > 0 ? varColumns.slice(0, expectedParamsCount) : [];
        const vars = activeColumns.map(h => String(r[h] ?? ''));
        entries.push({ phone, vars });
      }
    }
    
    if (!selectedTemplate) {
      setError('Please select a template.');
      return;
    }
    
    if (!campaignName || campaignName.trim() === '') {
      setError('Please enter a campaign name.');
      return;
    }

    if (templateNeedsMediaHeader.required) {
      const link = (mediaLink || '').trim();
      const hasId = !!(headerMediaMetaId || '').trim();
      // No longer require the user to provide media — backend auto-extracts from Meta template.
      // Only validate if user explicitly chose to override with their own file/URL.
      if (hasId && link) {
        setError('Use either an uploaded file or a URL, not both. Clear one to continue.');
        return;
      }
      if (link && !link.toLowerCase().startsWith('https://')) {
        setError('Header media URL must start with https:// (required by WhatsApp).');
        return;
      }
      if (link || hasId) {
        const fmt = templateNeedsMediaHeader.format;
        const mt = (mediaType || fmt || '').toLowerCase();
        if (!['image', 'video', 'audio', 'document'].includes(mt)) {
          setError('Select the media type that matches this template header (image, video, document, or audio).');
          return;
        }
        if (fmt && mt !== fmt) {
          setError(`This template uses a ${fmt.toUpperCase()} header — select "${fmt}" as the media type.`);
          return;
        }
      }
    }

    if (entries.length === 0) {
      setError('Please provide at least one recipient phone number (manual or CSV).');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Validate numbers (local); filter to valid only
      const { data: validation } = await validateNumbers(entries.map(e => e.phone));
      const validFlags = validation.map(v => v.valid);
      const filtered = entries.filter((_, i) => !!validFlags[i]);
      const removed = entries.length - filtered.length;
      
      // Use E.164 formatted numbers from validation (e.g., +919787613550)
      const recipientList = validation.filter(v => v.valid && v.e164).map(v => v.e164);
      const varsList = filtered.map(e => e.vars);

      if (recipientList.length === 0) {
        setError('No valid phone numbers found. Please check the format.');
        return;
      }

      const opts = { 
        campaign_name: campaignName.trim(),
        language 
      };
      if (varsList.length && varsList.some(v => v.length > 0)) {
        opts.variables = varsList;
      }
      if (mediaType && headerMediaMetaId) {
        opts.media_header = { type: mediaType, id: headerMediaMetaId };
      } else if (mediaType && (mediaLink || '').trim()) {
        opts.media_header = { type: mediaType, link: (mediaLink || '').trim() };
      } else if (templateNeedsMediaHeader.required && selectedTemplateData.autoMediaUrl) {
        opts.media_header = { type: templateNeedsMediaHeader.format, link: selectedTemplateData.autoMediaUrl };
      }
      if (schedule && scheduledAt) {
        const dt = new Date(scheduledAt);
        opts.scheduled_at = dt.toISOString();
      }
      
      const { data } = await sendCampaign(selectedTemplate, recipientList, opts);
      const scheduledNote = data.scheduled_at ? ` scheduled at ${new Date(data.scheduled_at).toLocaleString()}` : '';
      setMessage(`${data.status}: "${campaignName}" with ${recipientList.length} recipient${recipientList.length > 1 ? 's' : ''}${removed ? ` (${removed} invalid filtered)` : ''}${scheduledNote}`);
      
      // Prepend the new campaign
      setCampaigns(prev => [{ 
        id: data.campaign_id,
        campaign_name: campaignName,
        template_name: selectedTemplate, 
        language, 
        created_at: new Date().toISOString(), 
        total: recipientList.length, 
        counts: {}, 
        status: data.scheduled_at ? 'scheduled' : 'initiating', 
        scheduled_at: data.scheduled_at || null,
        sent_count: 0,
        last_sent_at: null,
      }, ...prev]);
      
      // Clear form
      setCampaignName('');
      setRecipients('');
      setCsvText(''); 
      setCsvHeaders([]); 
      setRows([]); 
      setPhoneColumn(''); 
      setVarColumns([]); 
      setCsvFileName(''); 
      setMediaType('');
      setMediaLink('');
      clearHeaderMediaUpload();
      setValidated({ total: 0, valid: 0, invalid: 0, samples: [], allResults: [] });
      setSchedule(false); 
      setScheduledAt('');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'An error occurred while sending the campaign.';
      setError(errorMsg);
      console.error('Campaign send error:', err);
      console.error('Error response:', err.response);
    } finally {
      setLoading(false);
    }
  };

  // CSV parsing (simple, no external deps)
  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (!lines.length) return { headers: [], rows: [] };
    const headers = lines[0].split(',').map(h => h.trim());
    const dataRows = lines.slice(1).map(line => {
      const parts = line.split(',');
      const obj = {};
      headers.forEach((h, i) => obj[h] = (parts[i] ?? '').trim());
      return obj;
    });
    return { headers, rows: dataRows };
  };

  const handleCsvChange = (e) => {
    const text = e.target.value;
    setCsvText(text);
    const { headers, rows } = parseCsv(text);
    setCsvHeaders(headers);
    setRows(rows);
    if (headers.length && !phoneColumn) setPhoneColumn(headers[0]);
    setVarColumns(headers.filter(h => h !== phoneColumn).slice(0, 5));
  };

  const handleCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    const text = await file.text();
    setCsvText(text);
    const { headers, rows } = parseCsv(text);
    setCsvHeaders(headers);
    setRows(rows);
    if (headers.length) {
      setPhoneColumn(headers[0]);
      setVarColumns(headers.slice(1, 6));
    }
  };

  const runValidation = async () => {
    const listFromText = recipients.split(/[\n,;]+/).map(r => r.trim()).filter(r => r);
    const listFromCsv = rows.map(r => String(r[phoneColumn] || '').trim()).filter(Boolean);
    const all = [...listFromText, ...listFromCsv];
    if (!all.length) { 
      setValidated({ total: 0, valid: 0, invalid: 0, samples: [] }); 
      setError('Please enter phone numbers or upload a CSV file first.');
      return; 
    }
    try {
      const { data } = await validateNumbers(all, 'US', useMetaValidation);
      const valid = data.filter(d => d.valid);
      const invalid = data.filter(d => !d.valid);
      setValidated({ total: all.length, valid: valid.length, invalid: invalid.length, samples: data.slice(0, 5), allResults: data });
    } catch (err) {
      setError('Failed to validate numbers. Please try again.');
      console.error(err);
    }
  };

  const downloadValidationCsv = () => {
    if (!validated.allResults || !validated.allResults.length) return;
    
    let csvContent = "Input Number,Status,E.164 Format,Country\n";
    
    validated.allResults.forEach(r => {
      const status = r.valid ? "Valid" : "Invalid";
      const e164 = r.e164 || "";
      const country = r.country_code ? `+${r.country_code}` : "";
      csvContent += `${r.input},${status},${e164},${country}\n`;
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "validation_results.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const selectedTemplateBody = useMemo(() => {
    const t = templates.find(t => t.name === selectedTemplate);
    const body = (t?.components || []).find(c => c.type === 'BODY')?.text || '';
    return body;
  }, [templates, selectedTemplate]);

  const selectedTemplateData = useMemo(() => {
    const t = templates.find(t => t.name === selectedTemplate);
    if (!t) return {};
    const comps = t.components || [];
    const headerComp = comps.find(c => (c.type || '').toUpperCase() === 'HEADER');
    const footerComp = comps.find(c => (c.type || '').toUpperCase() === 'FOOTER');
    const buttonsComp = comps.find(c => (c.type || '').toUpperCase() === 'BUTTONS');
    
    let autoMediaUrl = null;
    if (headerComp && headerComp.example) {
      if (Array.isArray(headerComp.example.header_handle) && headerComp.example.header_handle[0]) {
        autoMediaUrl = headerComp.example.header_handle[0];
      } else if (Array.isArray(headerComp.example.header_url) && headerComp.example.header_url[0]) {
        autoMediaUrl = headerComp.example.header_url[0];
      }
      if (!autoMediaUrl && typeof headerComp.example.header_handle === 'string') {
        autoMediaUrl = headerComp.example.header_handle;
      }
    }

    return {
      header: headerComp?.format === 'TEXT' ? headerComp.text : null,
      footer: footerComp?.text || null,
      buttons: buttonsComp?.buttons || [],
      headerMediaType: headerComp && headerComp.format !== 'TEXT' ? headerComp.format?.toLowerCase() : null,
      autoMediaUrl
    };
  }, [templates, selectedTemplate]);

  const expectedParamsCount = useMemo(() => {
    const t = templates.find(t => t.name === selectedTemplate);
    const components = t?.components || [];
    let max = 0;
    for (const comp of components) {
      const ctype = (comp.type || '').toUpperCase();
      if (ctype === 'BODY' || ctype === 'HEADER') {
        const matches = (comp.text || '').match(/\{\{(\d+)\}\}/g);
        if (matches) {
          for (const m of matches) {
            const num = parseInt(m.replace(/\D/g, ''), 10);
            if (num > max) max = num;
          }
        }
      }
    }
    return max;
  }, [templates, selectedTemplate]);

  /** Meta templates with HEADER format IMAGE/VIDEO/DOCUMENT/AUDIO need media on each send (upload in Blastmate or https link). */
  const templateNeedsMediaHeader = useMemo(() => {
    const t = templates.find(t => t.name === selectedTemplate);
    const header = (t?.components || []).find(c => (c.type || '').toUpperCase() === 'HEADER');
    const fmt = (header?.format || '').toUpperCase();
    if (['IMAGE', 'VIDEO', 'DOCUMENT', 'AUDIO'].includes(fmt)) {
      return { required: true, format: fmt.toLowerCase() };
    }
    return { required: false, format: null };
  }, [templates, selectedTemplate]);

  useEffect(() => {
    if (templateNeedsMediaHeader.required && templateNeedsMediaHeader.format) {
      setMediaType(templateNeedsMediaHeader.format);
    } else {
      setMediaType('');
    }
  }, [selectedTemplate, templateNeedsMediaHeader]);

  useEffect(() => {
    setHeaderMediaMetaId('');
    setMediaUploadError('');
    setMediaLink('');
    setHeaderMediaPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  }, [selectedTemplate]);

  const clearHeaderMediaUpload = () => {
    setHeaderMediaMetaId('');
    setMediaUploadError('');
    setHeaderMediaPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  };

  const handleHeaderCampaignFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setMediaUploadError('');
    setMediaLink('');
    setHeaderMediaMetaId('');
    setHeaderMediaPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    const objectUrl = URL.createObjectURL(file);
    setHeaderMediaPreviewUrl(objectUrl);
    setMediaUploading(true);
    try {
      const { data } = await uploadWaCampaignMedia(file);
      setHeaderMediaMetaId(data.id || '');
      const mime = String(data.mime_type || file.type || '').toLowerCase();
      if (mime.startsWith('image/')) setMediaType('image');
      else if (mime.startsWith('video/')) setMediaType('video');
      else if (mime.startsWith('audio/')) setMediaType('audio');
      else if (mime.includes('pdf') || mime.includes('msword') || mime.includes('document')) setMediaType('document');
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || err.message || 'Upload failed');
      setMediaUploadError(msg);
      setHeaderMediaMetaId('');
      URL.revokeObjectURL(objectUrl);
      setHeaderMediaPreviewUrl('');
    } finally {
      setMediaUploading(false);
    }
  };

  const previewMedia = useMemo(() => {
    const activeMediaType = mediaType || selectedTemplateData.headerMediaType;
    if (headerMediaPreviewUrl && activeMediaType) return { type: activeMediaType, link: headerMediaPreviewUrl };
    if (activeMediaType && (mediaLink || '').trim()) return { type: activeMediaType, link: (mediaLink || '').trim() };
    const hm = selectedTemplateData.headerMediaType;
    if (hm) {
      return { type: hm, link: selectedTemplateData.autoMediaUrl };
    }
    return null;
  }, [headerMediaPreviewUrl, mediaType, mediaLink, selectedTemplateData]);

  const handleDeleteCampaign = async (campaignId) => {
    if (!campaignId) return;
    if (!window.confirm('Are you sure you want to delete this campaign? This will also remove its message history.')) {
      return;
    }
    setDeletingId(campaignId);
    setError('');
    setMessage('');
    try {
      await deleteCampaign(campaignId);
      setCampaigns(prev => prev.filter(c => c.id !== campaignId));
      setMessage('Campaign deleted successfully.');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to delete campaign.';
      setError(errorMsg);
      console.error('Delete campaign error:', err);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <DashboardContainer>
      <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>
      <div className="page-toolbar">
        <div className="page-header">
          <div>
            <h1>Send a Campaign</h1>
            <p>Send an approved message template to multiple recipients.</p>
          </div>
          <div className="page-header-actions">
            {/* No extra actions here, placeholder for future buttons */}
          </div>
        </div>
      </div>

     {/* Top section: form (left) and live preview (right) */}
  <div className="campaigns-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 16, marginTop: 8 }}>
       <div className="campaigns-top-left">
  <div className="card padded card-full-height" style={{ marginTop: 8 }}>
           <h3 style={{marginTop:0}}>Send a Campaign</h3>
            <form onSubmit={handleSend} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
             
             {/* Step 1: Campaign Name */}
             <div style={{ marginBottom: '20px' }}>
              <label htmlFor="campaign-name" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                 1. Enter Campaign Name
               </label>
              <input
                id="campaign-name"
                type="text"
                placeholder="e.g., Summer Sale 2025, Welcome Message, etc."
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Give this campaign a memorable name for tracking
              </small>
            </div>
             
             {/* Step 2: Template Selection */}
             <div style={{ marginBottom: '20px' }}>
              <label htmlFor="template-select" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                 2. Select a Message Template
               </label>
              {templates.length > 0 ? (
                <select
                  id="template-select"
                  style={{ width: '100%' }}
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="select"
                >
                   {templates.map(t => (
                     <option key={t.name} value={t.name}>
                       {t.name} ({t.language})
                     </option>
                   ))}
                 </select>
               ) : (
                 <p>Loading templates or none found...</p>
               )}
             </div>

             <div style={{ marginBottom: '20px' }}>
               <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>3. Language</label>
               <input value={language} onChange={e => setLanguage(e.target.value)} placeholder="en_US" className="input" />
             </div>

             <div style={{ marginBottom: '20px' }}>
               <label htmlFor="recipients-textarea" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                 4. Enter Recipient Phone Numbers
               </label>
               <textarea
                 id="recipients-textarea"
                 value={recipients}
                 onChange={(e) => setRecipients(e.target.value)}
                 placeholder="Enter phone numbers with country code, separated by commas, semicolons, or new lines. Example: +919787613550"
                 rows="6"
                 className="textarea"
               />
               <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                 Enter phone numbers manually or upload a CSV file below
               </div>
             </div>

             <div style={{ marginBottom: '20px' }}>
               <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>5. Or Upload CSV File (Optional)</label>
               <input 
                 type="file" 
                 accept=".csv" 
                 onChange={handleCsvFile} 
                 className="input" 
                 style={{ marginBottom: 8, padding: 8 }} 
               />
               {csvFileName && (
                 <div style={{ fontSize: 12, color: '#00a884', marginBottom: 8, fontWeight: 500 }}>
                   ✓ {csvFileName} ({rows.length} rows)
                 </div>
               )}
               {csvHeaders.length > 0 && (
                 <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                   <div>
                     <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>Phone column</div>
                     <select value={phoneColumn} onChange={e => setPhoneColumn(e.target.value)} className="select">
                       {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                     </select>
                   </div>
                   <div>
                     <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>Map variables to columns</div>
                     <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                       {Array.from({ length: Math.min(expectedParamsCount, 5) }).map((_, i) => (
                         <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                           <div style={{ width: 60, fontSize: 12, color: '#555' }}>{`{{${i+1}}}`}</div>
                           <select
                             value={varColumns[i] || ''}
                             onChange={e => {
                               const arr = [...varColumns];
                               arr[i] = e.target.value;
                               setVarColumns(arr);
                             }}
                             className="select"
                             style={{ flex: 1 }}
                           >
                             <option value="">—</option>
                             {csvHeaders.filter(h => h !== phoneColumn).map(h => <option key={h} value={h}>{h}</option>)}
                           </select>
                         </div>
                       ))}
                     </div>
                   </div>
                 </div>
               )}
               <div style={{ marginTop: 12 }}>
                 <button type="button" onClick={runValidation} className="btn" disabled={rows.length === 0 && !recipients}>
                   Validate Numbers
                 </button>
                 {validated.total > 0 && (
                   <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                     <div style={{ fontSize: 12, color: '#444' }}>
                       {validated.valid} valid / {validated.invalid} invalid of {validated.total} numbers
                     </div>
                     <button type="button" onClick={downloadValidationCsv} className="btn" style={{ padding: '4px 8px', fontSize: 12 }}>
                       Download CSV Report
                     </button>
                   </div>
                 )}
                 <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 12, fontSize: 12 }}>
                   <input type="checkbox" checked={useMetaValidation} onChange={e => setUseMetaValidation(e.target.checked)} /> Use Meta validation (checks WhatsApp availability)
                 </label>
               </div>
             </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  {templateNeedsMediaHeader.required ? '6. Media Header' : '6. Optional media header'}
                </label>
                {templateNeedsMediaHeader.required && (
                  <div style={{ fontSize: 12, color: '#1b5e20', marginBottom: 8, padding: '8px 10px', background: '#e8f5e9', borderRadius: 6 }}>
                    {'✅ '}<strong>Auto-attached:</strong>{' Blastmate will automatically use the '}{templateNeedsMediaHeader.format?.toUpperCase()}{' from your approved Meta template. No need to upload anything.'}
                  </div>
                )}
                <details style={{ fontSize: 13, marginTop: 4 }}>
                  <summary style={{ cursor: 'pointer', color: '#008069' }}>Override with a different file (optional)</summary>
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                      <input type="file" onChange={handleHeaderCampaignFile} disabled={mediaUploading} className="input" style={{ maxWidth: '100%' }} />
                      {mediaUploading && <span style={{ fontSize: 12, color: '#666' }}>Uploading...</span>}
                      {headerMediaMetaId && !mediaUploading && (
                        <span style={{ fontSize: 12, color: '#00a884', fontWeight: 600 }}>Ready (Meta media id)</span>
                      )}
                      {(headerMediaMetaId || headerMediaPreviewUrl) && (
                        <button type="button" className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={clearHeaderMediaUpload}>Clear upload</button>
                      )}
                    </div>
                    {mediaUploadError && <div style={{ fontSize: 12, color: '#c62828' }}>{mediaUploadError}</div>}
                    <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>Or paste a public https URL:</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
                      <select value={mediaType} onChange={(e) => setMediaType(e.target.value)} className="select">
                        <option value="">None</option>
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                        <option value="audio">Audio</option>
                        <option value="document">Document</option>
                      </select>
                      <input
                        placeholder="https://example.com/your-file.jpg"
                        value={mediaLink}
                        onChange={(e) => {
                          setMediaLink(e.target.value);
                          if ((e.target.value || '').trim()) {
                            setHeaderMediaMetaId('');
                            if (headerMediaPreviewUrl) { URL.revokeObjectURL(headerMediaPreviewUrl); setHeaderMediaPreviewUrl(''); }
                          }
                        }}
                        className="input"
                      />
                    </div>
                  </div>
                </details>
                {schedule && headerMediaMetaId && (
                  <div style={{ fontSize: 11, color: '#856404', marginTop: 6 }}>
                    Note: Meta media ids expire. If scheduled far ahead, re-upload before send.
                  </div>
                )}
              </div>

             {/* Schedule section */}
             <div style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
               <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                 <input type="checkbox" checked={schedule} onChange={e => setSchedule(e.target.checked)} /> Schedule for later
               </label>
               {schedule && (
                 <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="input" />
               )}
             </div>

             <button type="submit" disabled={loading || mediaUploading} className="btn primary block">
               {loading ? 'Sending...' : (schedule ? 'Schedule Campaign' : 'Send Campaign')}
             </button>

             {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}
             {message && <p style={{ color: 'green', textAlign: 'center' }}>{message}</p>}
           </form>
         </div>
       </div>
       <div className="campaigns-top-right">
         <div className="card padded card-full-height" style={{ position: 'sticky', top: 20, maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', marginTop: 8 }}>
           <div style={{ fontSize: 13, color: '#444', marginBottom: 8 }}>Live preview</div>
           <TemplatePreview
              header={selectedTemplateData.header}
              body={selectedTemplateBody}
              footer={selectedTemplateData.footer}
              buttons={selectedTemplateData.buttons}
              media={previewMedia}
            />
         </div>
       </div>
     </div>

      {/* Bottom: recent campaigns list */}
      <div className="card padded" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{fontWeight:600}}>Recent Campaigns</div>
        </div>
        <div className="campaigns-list">
          {campaigns.map(c => {
            const counts = c.counts || {};
            const dispatchedCount = typeof c.sent_count === 'number' ? c.sent_count : (counts.sent ?? counts.SENT ?? 0);
            const deliveredCount = typeof c.delivered_count === 'number' ? c.delivered_count : (counts.delivered ?? counts.DELIVERED ?? 0);
            const readCount = typeof c.read_count === 'number' ? c.read_count : (counts.read ?? counts.READ ?? 0);
            const failedCount = typeof c.failed_count === 'number' ? c.failed_count : (counts.failed ?? counts.FAILED ?? 0);

            return (
            <div key={c.id} className="campaign-card card padded">
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                {c.campaign_name || c.template_name}
              </div>
              <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                Template: {c.template_name}
              </div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  {new Date(c.created_at).toLocaleString()} · {c.language}
                  {c.last_sent_at && (
                    <span style={{ marginLeft: 6, color: '#444' }}>
                      · Last sent {new Date(c.last_sent_at).toLocaleString()}
                    </span>
                  )}
              </div>
              {c.status && (
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  <strong>Status:</strong> <span style={{ 
                    color: c.status === 'completed' ? 'green' : 
                           c.status === 'processing' || c.status === 'initiating' ? 'orange' : 
                           c.status === 'failed' ? 'red' : 'blue',
                    fontWeight: 600
                  }}>{c.status.toUpperCase()}</span>
                  {c.scheduled_at ? ` · Scheduled: ${new Date(c.scheduled_at).toLocaleString()}` : ''}
                </div>
              )}
              {c.last_error_message && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#d32f2f', backgroundColor: '#fdecea', padding: '6px 8px', borderRadius: 4 }}>
                  <strong>Error:</strong> {c.last_error_message}
                </div>
              )}
                <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6, fontSize: 13 }}>
                  <div><strong>Total:</strong> {c.total}</div>
                  <div><strong>Dispatched:</strong> {dispatchedCount}</div>
                  <div><strong>Delivered:</strong> {deliveredCount}</div>
                  <div><strong>Read:</strong> {readCount}</div>
                  <div><strong>Failed:</strong> {failedCount}</div>
                </div>
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="btn danger"
                    style={{ padding: '6px 12px', fontSize: 12 }}
                    onClick={() => handleDeleteCampaign(c.id)}
                    disabled={deletingId === c.id}
                  >
                    {deletingId === c.id ? 'Deleting…' : 'Delete Campaign'}
                  </button>
                </div>
            </div>
            );
          })}
          {campaigns.length === 0 && (
            <div style={{ color: '#666' }}>No campaigns yet.</div>
          )}
        </div>
      </div>
      </div>
    </DashboardContainer>
  );
};

export default CampaignsPage;
